import { useEffect, useMemo, type ReactNode } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import { useUiStore } from "@/stores/ui-store";
import { messagesForLocale } from "./messages";

// react-intl provider tied to the UI store's locale. Both bundles
// (~27KB combined) load eagerly - the legacy lazy-load path was a
// Next.js SSR concern that no longer applies.
//
// Bundles are stored as flat dotted keys — key == message id (@adr SPARK-0025-i18n-bundle-single-flat-representation@1).
// flatten() is therefore effectively a pass-through, but kept defensive: it
// still collapses any nested object, and bundle-parity.test.ts fails if a
// nested block is reintroduced. Messages are referenced everywhere by their
// dot-notation id.
//
// LangSync side-effect: keeps document.documentElement.lang in sync
// with the active locale. Accessibility tooling + browser features
// (hyphenation, spell-check, screen reader voice) all read this.

export function IntlProvider({ children }: { children: ReactNode }) {
  const locale = useUiStore((s) => s.locale);
  const messages = useMemo(() => messagesForLocale(locale).messages, [locale]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  return (
    <ReactIntlProvider
      locale={locale}
      messages={messages}
      defaultLocale="en"
      // Translation IDs that don't exist in the active bundle render
      // as the ID rather than throwing. In dev this is loud; in
      // production it's tolerable degradation.
      onError={(err) => {
        if (import.meta.env.DEV) {
          console.warn("[intl]", err.message);
        }
      }}
    >
      {children}
    </ReactIntlProvider>
  );
}
